import type { components } from "@/api/booqly-api";
import { useTenantMe } from "./tenant-me";

export type PlanUsage = components["schemas"]["PlanUsageDto"];
export type PlanLimits = components["schemas"]["PlanLimitsDto"];
export type PlanCurrent = components["schemas"]["PlanCurrentDto"];

/**
 * Reuses the `/tenants/me` query so there's no extra fetch — the
 * usage block is part of the same payload. Returns `undefined` while
 * the query is loading so callers can treat absence as "don't render
 * the banner yet."
 */
export function usePlanUsage(): PlanUsage | undefined {
  const me = useTenantMe();
  return me.data?.usage;
}

export function isAtServiceCap(u: PlanUsage | undefined): boolean {
  if (!u) return false;
  const cap = u.limits.maxServices;
  if (cap == null) return false;
  return u.current.services >= cap;
}

export function isAtSeatCap(u: PlanUsage | undefined): boolean {
  if (!u) return false;
  const cap = u.limits.maxOperators;
  if (cap == null) return false;
  return u.current.operators >= cap;
}

export function bookingsUtilization(u: PlanUsage | undefined): number | null {
  if (!u) return null;
  const cap = u.limits.publicBookingsPerWeek;
  if (cap == null) return null;
  if (cap <= 0) return 1;
  return Math.min(1, u.current.publicBookingsThisWeek / cap);
}

export function seatsUtilization(u: PlanUsage | undefined): number | null {
  if (!u) return null;
  const cap = u.limits.maxOperators;
  if (cap == null) return null;
  if (cap <= 0) return 1;
  return Math.min(1, u.current.operators / cap);
}

export function planDisplayName(plan: string): string {
  switch (plan) {
    case "STARTER": return "Starter";
    case "GROWTH":  return "Growth";
    case "PRO":     return "Pro";
    default:        return plan;
  }
}
